import {IGameShowResponse} from "@webng-types/write-model";
import {LiveblogThemeConfiguration, liveblogThemeConfigurationSchema} from "./liveblog";
import {LiveblogTeaserThemeConfiguration, liveblogTeaserThemeConfigurationSchema} from "./teaser";
import {SharedThemeConfiguration, sharedThemeConfigurationSchema} from "./shared";
import z from "zod";
import {removeNullValues} from "./utils";

export type {LiveblogThemeConfiguration, SortOrder, RefreshType, AnalyticsThemeConfiguration} from "./liveblog";
export type {SharedThemeConfiguration} from "./shared";

export type {LiveblogTeaserThemeConfiguration, AnalyticsTeaserThemeConfiguration} from "./teaser";
export {liveblogWidgetConfigurationLimitMax, liveblogWidgetConfigurationLimitMin, sortValues, refreshValues, analyticsLiveblogThemeKeys, buildAnalyticsThemeConfiguration} from "./liveblog";
export {analyticsTeaserThemeKeys, buildAnalyticsTeaserThemeConfiguration} from "./teaser";


// Liveblog Core Widget

export interface LiveblogCoreWidgetInitialData {
  gameShowResponse?: IGameShowResponse
  uniqueId?: string
  isSSR?: boolean
  /**
   * The page is hosted by Tickaroo rather than by the publisher — today only the hosted fullpage
   * (`/embed/v4/fullpage/…`, the "Liveblog Link" feature).
   *
   * Travels in `initialData` rather than as a `<tickaroo-liveblog>` attribute on purpose: it is
   * something the server knows and the client needs, not part of the embed's public contract that
   * publishers set. It has to reach the client at all because the same widget bundle serves both
   * the hosted fullpage and publisher embeds, so the client render context cannot tell where it is.
   *
   * Currently consumed only by the Viafoura gate: Viafoura resolves the account from the page's
   * hostname, and Tickaroo's hostnames are deliberately not provisioned with it.
   */
  tickarooHosted?: boolean
}

// Liveblog Widget


export interface LiveblogWidgetInitialData extends LiveblogCoreWidgetInitialData {
  configuration?: LiveblogThemeConfiguration & SharedThemeConfiguration
  liveblogThemeConfiguration?: LiveblogThemeConfiguration,
  sharedThemeConfiguration?: SharedThemeConfiguration,
  css?: string
  liveblogUrl?: string
  schemaOrg?: string
}

export interface LiveblogTeaserWidgetInitialData extends LiveblogCoreWidgetInitialData {
  configuration?: LiveblogTeaserThemeConfiguration & SharedThemeConfiguration
  liveblogTeaserThemeConfiguration: LiveblogTeaserThemeConfiguration,
  sharedThemeConfiguration: SharedThemeConfiguration,
  css?: string
  liveblogUrl?: string
}

// OUTPUT CHANNEL TEMPLATES

export const rawThemeSchema = z.object({
  _id: z.string(),
  name: z.string(),
  sharedConfiguration: sharedThemeConfigurationSchema,
  liveblogConfiguration: liveblogThemeConfigurationSchema,
  liveblogTeaserConfiguration: liveblogTeaserThemeConfigurationSchema,
  customStyleId: z.string().optional(),
  ownerId: z.string(),
  isDefault: z.boolean().optional(),
  createdAt: z.number().optional(),
  createdBy: z.string().optional(),
  updatedAt: z.number().optional(),
  updatedBy: z.string().optional(),
  deletedAt: z.number().optional(),
  deletedBy: z.string().optional(),
})

export const themeSchema = z.preprocess(removeNullValues, rawThemeSchema)

export type Theme = z.infer<typeof rawThemeSchema>

export interface ThemeList<T extends Theme = Theme> {
  items: T[]
}

export const themeUpdatePayloadSchema = rawThemeSchema.pick({
  name: true,
  sharedConfiguration: true,
  liveblogConfiguration: true,
  liveblogTeaserConfiguration: true,
  customStyleId: true,
})

export type ThemeUpdatePayload = z.infer<typeof themeUpdatePayloadSchema>

export const themeCreatePayloadSchema = rawThemeSchema.pick({
  name: true,
  sharedConfiguration: true,
  liveblogConfiguration: true,
  liveblogTeaserConfiguration: true,
  customStyleId: true,
}).extend({
  _id: z.string().optional()
})

export type ThemeCreatePayload = z.infer<typeof themeCreatePayloadSchema>


export interface CustomStyle {
  _id: string
  ownerId: string
  name: string
  css: string
  createdAt?: number
  updatedAt?: number
  updatedBy?: string
  deletedAt?: number
  deletedBy?: string
}

export interface CustomStyleList {
  items: CustomStyle[]
}

export interface CustomStyleUpdatePayload {
  name: string
  css: string
}

export interface CustomStyleCreatePayload {
  name: string
  css: string
}

export interface IMultiMediaDisplayTypeOption {
  label: string
  value: string
}

export enum MultiMediaDisplayTypeOptionEnums {
  DEFAULT = "default",
  SLIDESHOW = "slideshow",
}

export function ensureThemeDefaults<T extends Theme = Theme>(theme: T): T {
  return themeSchema.parse(theme) as T
}
